import CryptoJS from 'crypto-js';

const CALLBACK_HASH_SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

function canonicalPayload(data) {
  const source = data && typeof data === 'object' ? { ...data } : {};
  delete source.hmac;
  delete source.userHmac;
  delete source.mac;
  Object.keys(source).forEach((key) => {
    if (source[key] === undefined) delete source[key];
  });

  if (Array.isArray(source.actions)) {
    source.actions = source.actions
      .map((action) =>
        Object.keys(action)
          .sort()
          .map((key) => action[key])
          .join('')
      )
      .join('');
  }

  return Object.keys(source)
    .sort()
    .map((key) => source[key])
    .join('');
}

export function createNewHmac(data) {
  if (!CALLBACK_HASH_SECRET_KEY) {
    throw new Error("HMAC Secret Key is missing in environment variables.");
  }

  const hashResp = CryptoJS.SHA256(CALLBACK_HASH_SECRET_KEY);
  const hmacResp = CryptoJS.HmacSHA256(canonicalPayload(data), hashResp);
  return CryptoJS.enc.Hex.stringify(hmacResp);
}

function readHeader(headers, name) {
  if (!headers) return '';
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    if (value) return value;
  }
  const target = name.toLowerCase();
  const keys = Object.keys(headers);
  for (let i = 0; i < keys.length; i += 1) {
    if (keys[i].toLowerCase() === target && headers[keys[i]]) {
      return headers[keys[i]];
    }
  }
  return '';
}

function writeHeader(headers, name, value) {
  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }
  headers[name] = value;
}

function requestPath(url, base) {
  try {
    return new URL(url, base || undefined).pathname;
  } catch (e) {
    return String(url || '').split('?')[0];
  }
}

function isLoginStart(pathname) {
  return pathname === '/login' || pathname === '/login/admin' || pathname === '/admin/login';
}

function createNonce() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function applyRequestHmac(config) {
  const base = process.env.REACT_APP_API_BASE_URL || '';
  const rawUrl = config.url || '';
  if (base && !String(rawUrl).startsWith(base)) {
    return config;
  }

  const method = String(config.method || 'GET').toUpperCase();
  config.headers = config.headers || {};
  if (readHeader(config.headers, 'userHmac')) return config;

  const data = config.data;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return config;
  if (data && typeof data === 'object' && data.userHmac) return config;

  if (method === 'GET' || method === 'HEAD' || method === 'DELETE') {
    const authorization = readHeader(config.headers, 'Authorization');
    if (!authorization) return config;
    const time = String(Date.now());
    const nonce = createNonce();
    const path = requestPath(rawUrl, base);
    writeHeader(config.headers, 'userHmac', createNewHmac({
      token: authorization,
      time,
      nonce,
      path,
      method,
    }));
    writeHeader(config.headers, 'x-request-time', time);
    writeHeader(config.headers, 'x-request-nonce', nonce);
    return config;
  }

  if ((method === 'POST' || method === 'PUT' || method === 'PATCH') && data && typeof data === 'object') {
    const path = requestPath(rawUrl, base);
    data.time = String(Date.now());
    data.nonce = createNonce();
    data.path = path;
    data.method = method;
    if (isLoginStart(path) && data.newTime == null) {
      data.newTime = Date.now();
    }
    data.userHmac = createNewHmac(data);
    config.data = data;
  }

  return config;
}
